import type React from "react"
import { Table, Tag, Typography, type TablePaginationConfig, type TableProps } from "antd"
import {
  IconFolder,
  IconFolderOpen,
  IconClick,
  IconArrowBackUp,
  IconKeyboard,
  IconExternalLink,
} from "@tabler/icons-react"
import { UserCellLink } from "@/components/UserCellLink"
import { DeviceBadge } from "@/components/DeviceBadge"
import { useTableScroll } from "@/hooks/useTableScroll"
import { WEB_URL } from "@/configs/config"
import type { SetSearchLogItem } from "@/services/log/setSearchLogApi"
import dayjs from "dayjs"
import "@/styles/table.full.css"

const { Text } = Typography

function toSlug(str?: string | null): string {
  if (!str) return "set"
  return (
    str
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[đĐ]/g, "d")
      .replace(/[^a-z0-9\s-]/g, "")
      .trim()
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-") || "set"
  )
}

interface SetSearchLogTableProps {
  data: SetSearchLogItem[]
  loading: boolean
  pagination?: TableProps<SetSearchLogItem>["pagination"]
}

export const SetSearchLogTable: React.FC<SetSearchLogTableProps> = ({ data, loading, pagination }) => {
  const { scrollConfig, tableWrapperRef } = useTableScroll()

  const columns: TableProps<SetSearchLogItem>["columns"] = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 75,
      align: "center",
      fixed: "left",
      render: (val: number) => <span className="font-mono text-gray-400 text-xs">#{val}</span>,
    },
    {
      title: "Người dùng",
      key: "user",
      width: 210,
      fixed: "left",
      render: (_, record: SetSearchLogItem) => (
        <UserCellLink user={record.user} userId={record.user_id} fallback={<Tag color="default">Khách</Tag>} />
      ),
    },
    {
      title: "Thời gian",
      dataIndex: "created_at",
      key: "created_at",
      width: 140,
      align: "center",
      render: (val: string) => {
        if (!val) return "—"
        const d = dayjs(val)
        return (
          <div className="flex flex-col font-mono text-xs leading-tight">
            <span className="font-medium text-gray-800">{d.format("DD/MM/YYYY")}</span>
            <Text type="secondary" className="text-[11px]">
              {d.format("HH:mm:ss")}
            </Text>
          </div>
        )
      },
    },
    {
      title: "Từ khóa tra cứu",
      dataIndex: "query",
      key: "query",
      width: 200,
      render: (val: string) => (
        <span className="inline-block max-w-[190px] truncate rounded border border-blue-200 bg-blue-50/70 px-2 py-0.5 font-semibold text-blue-900 text-sm">
          {val}
        </span>
      ),
    },
    {
      title: "Số KQ",
      dataIndex: "results_count",
      key: "results_count",
      width: 100,
      align: "center",
      render: (val: number) => {
        if (val === 0) {
          return (
            <Tag color="error" className="font-medium">
              0 kết quả
            </Tag>
          )
        }
        return (
          <Tag color="processing" className="font-medium">
            {val} bộ từ
          </Tag>
        )
      },
    },
    {
      title: "Tương tác",
      dataIndex: "action",
      key: "action",
      width: 130,
      align: "center",
      render: (val: string) => {
        if (val === "click") {
          return (
            <Tag color="success" icon={<IconClick size={13} className="mr-1 inline" />}>
              Click card
            </Tag>
          )
        }
        if (val === "enter") {
          return (
            <Tag color="blue" icon={<IconKeyboard size={13} className="mr-1 inline" />}>
              Ấn Enter
            </Tag>
          )
        }
        return (
          <Tag color="default" icon={<IconArrowBackUp size={13} className="mr-1 inline" />}>
            Blur (rời)
          </Tag>
        )
      },
    },
    {
      title: "Bộ từ được chọn",
      key: "selected_set",
      width: 280,
      render: (_, record: SetSearchLogItem) => {
        if (record.action !== "click" && !record.selected_set_id && !record.selected_set_title) {
          return <Text type="secondary">—</Text>
        }

        const title =
          record.selected_set_title ||
          record.selected_set?.title ||
          (record.selected_set_id ? `Bộ từ #${record.selected_set_id}` : "—")

        const isSystem = record.selected_set?.type === "system"
        const hasIndex = record.selected_index !== null && record.selected_index !== undefined

        const content = (
          <div className="flex flex-col gap-0.5">
            <div className="flex items-center gap-1.5">
              {isSystem ? (
                <IconFolderOpen size={16} className="shrink-0 text-blue-500" />
              ) : (
                <IconFolder size={16} className="shrink-0 text-purple-500" />
              )}
              <span className="truncate font-medium text-gray-900 text-sm">{title}</span>
            </div>
            <div className="flex items-center gap-1.5">
              {record.selected_set?.type && (
                <Tag color={isSystem ? "blue" : "purple"} className="text-[10px] leading-tight">
                  {isSystem ? "Hệ thống" : "Cá nhân"}
                </Tag>
              )}
              {hasIndex && (
                <span className="font-mono text-[11px] text-gray-500">Vị trí #{Number(record.selected_index) + 1}</span>
              )}
            </div>
          </div>
        )

        if (record.selected_set_id) {
          const slug = record.selected_set?.slug || toSlug(title)
          const webUrl = `${WEB_URL}/set/${slug}-${record.selected_set_id}`
          return (
            <a
              href={webUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex flex-col gap-0.5 text-gray-900 hover:text-blue-600"
              title={`Mở trang bộ từ trên web: ${webUrl}`}
            >
              <div className="flex items-center gap-1.5">
                {isSystem ? (
                  <IconFolderOpen size={16} className="shrink-0 text-blue-500" />
                ) : (
                  <IconFolder size={16} className="shrink-0 text-purple-500" />
                )}
                <span className="truncate font-medium text-sm group-hover:text-blue-600 group-hover:underline">
                  {title}
                </span>
                <IconExternalLink size={13} className="shrink-0 text-gray-400 group-hover:text-blue-500" />
              </div>
              <div className="flex items-center gap-1.5">
                {record.selected_set?.type && (
                  <Tag color={isSystem ? "blue" : "purple"} className="text-[10px] leading-tight">
                    {isSystem ? "Hệ thống" : "Cá nhân"}
                  </Tag>
                )}
                {hasIndex && (
                  <span className="font-mono text-[11px] text-gray-500">
                    Vị trí #{Number(record.selected_index) + 1}
                  </span>
                )}
              </div>
            </a>
          )
        }

        return content
      },
    },
    {
      title: "Thiết bị",
      dataIndex: "user_agent",
      key: "user_agent",
      width: 130,
      align: "center",
      render: (ua: string | null | undefined, record: SetSearchLogItem) => (
        <DeviceBadge userAgent={ua} ipAddress={record.ip_address} />
      ),
    },
    {
      title: "Độ trễ",
      dataIndex: "latency_ms",
      key: "latency_ms",
      width: 90,
      align: "center",
      render: (val?: number | null) => {
        if (val === null || val === undefined) return <Text type="secondary">—</Text>
        const color = val > 1000 ? "text-red-500 font-semibold" : val > 500 ? "text-amber-500 font-medium" : "text-emerald-600"
        return <span className={`font-mono text-xs ${color}`}>{val}ms</span>
      },
    },
  ]

  return (
    <div className="min-h-0 min-w-0 flex-1" ref={tableWrapperRef}>
      <Table<SetSearchLogItem>
        bordered
        rowKey="id"
        columns={columns}
        dataSource={data}
        loading={loading}
        pagination={pagination as TablePaginationConfig}
        scroll={{ x: "100%", y: scrollConfig.y }}
        size="small"
        classNames={{
          root: "full-height-table",
        }}
      />
    </div>
  )
}

export default SetSearchLogTable
